import { FeatureFlagsHelper } from './FeatureFlagsHelper';

/**
 * There are two types of "feature flags": Development-only flags, and permanent features.
 *
 * "Development" feature flags (features that are being built, for which the flags will be
 * removed once the feature is shipped) belong in this file.
 * ( more info here: https://mytableau.tableaucorp.com/display/devcomm/VizPortal+Client+-+Feature+Flags )
 *
 * Flags for permanent features should not live here; instead, those flags should live on the server
 * or site objects of ISessionInfo, depending on whether they can be toggled at the server or site
 * level.

 One day this will move from a guideline to a requirement and you will get an urgent bug to
 move your flag, if it falls into that category.
 *
 * (TFS 653138 - Move permanent feature flags out of IFeatureFlags and into server/site info)
 *
 * This module is a global object that is as low-level in the app as possible,
 * so we can theoretically put any parameter of the application behind a flag. Flags are specified
 * here, but can be overridden at runtime via URL query parameters.
 */
export module FeatureFlags {

  /**
   * Allows direct assignment of a remote query agent to a data source
   * Created: 2017-03-16 bjudkins
   */
  // remove: TFS 808766
  export const bridgeLiveDirectAssignEnabled = false;

  /**
   * Enables Admin Viz tab of bridge extract refresh
   * Created: 2018-09-18 xyao
   */
  // remove: TFS 692209
  export const bridgeExtractRefreshAdminVizEnabled = false;

  /**
   * Enables showing bridge client version on Bridge settings page
   * Created: 2018-10-08 fuliu
   */
  // remove: TFS 795501
  export const bridgeShowClientVersionEnabled = false;

  /**
   * Enables sending warning emails to customers if remote extract refresh is not completed on time
   * Created: 2019-03-25 fuliu
   */
  // remove: TFS 889404
  export const bridgeSendWarningForExtractRefresh = false;

  /**
   * Enable SSS widget for Bridge Extract Refresh of Tableau Online
   * Created: 2019-04-15 xyao
   */
  // remove: TFS 502707
  export const remoteRefreshSelfServiceScheduleWidgetEnabled = false;

  /**
  *  Enables the feature where user can use their OAuth credential to refresh WDC data souces.
  */
  // Created: 2015-10-16 wechen
  // Enabled: 2016-01-07 wechen
  // Disabled: 2016-02-09 mbgumport
  // remove: TFS 808766
  export const oauthWDCServerRefreshEnabled = false;

  // Enables creation of self service schedules
  // Created: 2016-10-24 dcoonan
  // remove: TFS 808776
  export const selfServiceSchedulesEnabled = false;

  // Enables the feature to create user time zone
  // Created: 2017-03-20 nchenglie
  export const userTimeZoneEnabled = false;

  /**
  * Enables the job management feature.
  */
  // Created: 2018-09-19 nchenglie
  export const jobManagementEnabled = false;

  /*
   * Shows the Certification information on the DS place page.
   */
  // Created: 2017-04-18 eprescott
  // remove: TFS 808701
  export const datasourceCertificationEnabled = true;

  /*
   * Shows a link to disk space usage admin view if enabled
   */
  // Created: 2016-01-11 jchar
  export const lowDiskSpaceEnabled = false;

  /*
   * Display Tableau desktop license usage summary if enabled
   */
  // Created: 2016-03-31 jili
  // Enabled: 2016-04-04 jili
  // remove: TFS 808741
  export const desktopReportingEnabled = true;

  /*
   * Allows subscriptions to be triggered based off of a threshold condition.
   */
  // Created: 2016-06-08 cpredmore
  // Enabled: 2016-09-29 cpredmore
  // Disabled: 2016-10-28 abrandon (TFS: 581467)
  // Enabled: 2017-01-24 tyakushev
  // remove: TFS 808745
  export const alertOnThresholdConditionEnabled = true;

  /*
   * Allows users to resume suspended Data Alerts.
   */
  // Created: 2018-09-25 hmughnetsyan
  // Enabled: 2019-01-14 osibiryov
  export const suspendDataAlerts = true;

  /*
   * Display the data alert privacy info if enabled
   */
  // Created: 2018-12-12 zszugyi
  // Enabled: 2019-01-14 kknightboehm
  export const displayDataAlertPrivacy = true;

  /*
   * Allows viewing Metrics by proxying out to an external Metrics Server.
   */
  // Created: 2017-06-12 cpredmore
  export const metricsProxyEnabled = false;

  /*
   * Allows navigating to Metrics Authoring
   */
  // Created: 2018-11-13 lascher
  export const metricsAuthoringPathEnabled = false;

  /*
   * Enables Metrics Space Navigation
   */
  // Created: 2019-02-02 alisanti
  export const metricsSpaceEnabled = false;

  /*
   * Allow UI actions for remove self from data alert
   */
  // Created: 2016-10-19 jfitzgerald
  // Enabled: 2016-11-30 abrandon
  // remove: TFS 775869
  export const removeSelfFromDataAlertEnabled = true;

  /*
   * Allow UI actions for adding self to data alert
   */
  // Created: 2016-10-19 jfitzgerald
  // Enabled: 2016-11-30 abrandon
  // remove: TFS 775869
  export const addSelfToDataAlertEnabled = true;

  /*
   * Allows Tableau support users to be added to a Tableau Online site.
   */
  export const addSupportUserEnabled = false;

  /**
   * Enabled Guest Account Per Site Functionality
   */
  //Created: 2016-08-22 preguraman
  //Enabled: 2016-09-26 preguraman
  // remove: TFS 762916
  export const guestAccountPerSiteEnabled = true;

  /**
   * Allows the server admin to enable the cache warmup feature
   *
  */
  // Created: 2016-09-23 gfilpus
  // Enabled: 2017-03-20 gfilpus
  // remove: TFS 774533
  export const cacheWarmup = true;

  /**
   * Enable "Refresh Now" menu item for remotely-refresh data sources
   * Created: 2016-12-08 bjudkins
   */
  // remove: 808766
  export const dataSyncRefreshNowEnabled = false;

  /**
   * Uses a new, vizportal-based getting started screen, rather than rendering a marketing page.
   * Created: 2016-09-29 cshiraishi
   * Enabled: 2016-11-22 cshiraishi
   */
  // Remove: TFS 775870
  export const newGettingStartedScreen = true;

  /**
   * Size the viz iFrame to the Size of the Window
   *
   * This flag is primarily used by clients (vizportal/vizclient)
   *
   */
  //Created: 2017-04-07 asainath
  export const iframeSizedToWindowEnabled = false;

  /**
   * Shows a custom banner for opening in app for mobile devices on the login page.
   */
  // Created: 2017-03-15
  // Enabled: 2017-03-17
  // remove: TFS 808698
  export const customAppBanner = true;

  /**
   * Shows 'what's new' menu option
   */
  // Created: 2017-04-06 klin
  export const whatIsNewMenuOptionEnabled = false;

  /**
   * Enable renaming views on server (without going into web authoring)
   */
  // Created: 2017-08-07 janlee
  export const renameViewsEnabled = false;

  /**
   * Shows the schedule editor for the recommendations trainer
   */
  // Created: 2017-04-20 wmaciel
  // Enabled: 2017-04-20 wmaciel
  // remove: TFS 808756
  export const recommendationsTrainerScheduleEnabled = true;

  /**
   * Show hyper instead of dataengine.
   */
  //Created: 2017-05-8 mroberts
  // remove: TFS 808750
  export const hyperOnServerEnabled = false;

  /**
   * Flag to know if new tabadmin services (TSM) feature is enabled (backing feature flag is TabadminService)
   */
  // Created: 2017-05-18 acarre
  // remove: TFS 808750
  export const tableauServicesManagerEnabled = false;

  /**
   * Enable action to create a new blank workbook (backing feature flag is WebCreateNewWorkbook)
   */
  // Created: 2017-08-04 jdeklotz
  export const webCreateNewWorkbookEnabled = false;

  /**
   * Display data connection tooltip in viz view.
   */
  // Created: 2017-08-16 shubbard
  export const dataConnectionTooltipInVizView = true;

  /**
   * Enable initalizing the semantic model for natural language processing (NLP)
   */
  // Created: 2017-11-07 jericson
  export const nlboxEnabled = false;

  /**
   * Enable keyboard shortcuts like / for search
   */
  // Created: 2017-11-29 jfitzgerald
  // Enabled: 2018-03-02 jfitzgerald
  export const keyboardShortcutsEnabled = true;

  /**
   * Send analytics info to server
   */
  // Created: 2017-11-21 jfitzgerald
  export const OnlineAnalyticsPipelineEnabled = false;

  /**
   * Flows subplace and Flow place, among other things.
   */
  // Created: 2017-12-22 jraymakers
  export const maestroServerV1Enabled = false;

  /**
   * Call the getPrincipalNames API to get user and group names together
   */
  // Created: 2017-12-27 jfitzgerald
  export const getGroupAndUserNamesEnabled = false;

  /**
   * Display SCIM settings in the authentication tab
   * See: http://l/tfs/697361
   */
  // Created: 2018-02-21 mvafadost
  export const scimApiTokenEnabled = false;

  /**
   * Preview Device Layouts on dashboards
   */
  // Created: 2018-03-06 majung
  export const PreviewDeviceLayouts = false;

  /**
   *  Show new site settings tab for extensions
   */
  // created: 2018-4-4 jfitzgerald
  export const extensionsSettingsTabEnabled = true;

  /**
   *  Enable/Disable Mentions in Commenting
   */
  // Created: 2018-4-30 nranganna
  export const commentingMentionsEnabled = true;

  /**
   * Show all the data catalog features
   */
  // created: 2018-5-4 dbecker
  // enabled: 2019-05-13 abrandon
  export const metadataAreaEnabled = true;

  /**
   * UI flag that controls setting database and
   * table contacts
   */
  // created: 2019-07-01 rlemma
  export const externalContentContactEnabled = true;

  /**
   * Enable/Disable remove external content action
   */
  export const removeExternalContentEnabled = true;

  /**
   * Favorite stars for flows in the flows list and place header.
   */
  // Created: 2018-06-13 jraymakers
  export const flowFavoritesEnabled = true;

  /**
   *  Permission action for datacatalog content, e.g. database and table.
   */
      // created: 2019-04-19 mechen
  export const dataCatalogPermissionEnabled = true;

  /**
   * When publishing a workbook, this flag controls the appearance
   * of a new dialog box that has updated styling and lets users jump
   * directly to device preview from the Publishing Complete dialog.
   */
  // Created: 2018-06-19 toverton
  export const newPublishCompleteDialogBox = true;

  /**
   * Show add active directory site users dialog react
   */
  //Created: 2018-07-30 rjoshi
  //Enabled: 2018-07-30 rjoshi
  export const addActiveDirectorySiteUsersDialogReactEnabled = true;

  /**
   * Show site add active directory servers users dialog react
   */
  //Created: 2018-07-16 rjoshi
  //Enabled: 2018-07-16 rjoshi
  export const addActiveDirectoryServerUsersDialogReactEnabled = true;

  /**
   * Show add local users dialog react
   */
  //Created: 2018-10-10 rjoshi
  //Enabled: 2018-11-1 rjoshi
  export const addLocalUsersDialogReactEnabled = true;

  /*
  * New UI for Flow Overview Page
  */
  // Created: 2018-8-7 jjia
  // Enabled: 2018-9-4 jjia
  // Removed: 2018-11-27 jjia
  export const flowOverviewUpdateEnabled = false;

  /**
   * Show suspend subscription status and allow users to resume suspended subscriptions
   */
  // Created: 2018-09-11 jvanduyne
  // Enabled: 2018-10-29 jvanduyne
  export const suspendSubscriptions = true;

  /**
   * Stylish browsing experience for flow place pages.
   */
  // Created: 2018-10-09 jraymakers
  export const stylishFlows = true;

  /**
   *  Show react modal for online add user instead of angularjs version
   */
  //Created: 2018-09-07 kkane
  //Enabled: 2018-09-07 kkane
  export const addExternalSiteUsersDialogReactEnabled = true;

  /**
   * Flow overview page and run history update every minute
   */
  // Created: 2018-09-07 jsigo
  export const autoUpdateFlowGrids = true;

  /**
   * Show suspended flow task status and allow users to resume suspended flows
   */
  // Created const 2018-10-17 jsigo
  export const autoSuspendFlowTasksEnabled = false;

  /**
   * Show set site users role dialog in react
   */
  //Created: 2018-10-12 kkane
  //Enabled: 2018-10-12 kkane
  export const SetSiteUsersRoleDialogReactEnabled = true;

  /**
   * Define policies that all new passwords must comply with.
   */
  // Created: 2018-10-31 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const passwordPoliciesEnabled = true;

  /**
   * TFS852547 - Sheets as Tabs Dashboards should preview like Phoenix
   * Mobile Web shows these dashboards with tabs.  Phoenix shows them without tabs.
   */
  // Created: 2018-12-06 majung
  export const SheetsAsTabsDashboardsPreviewLikePhoenix = true;

  /**
   * Make the password expire after a certain amount of days and force the user to reset it.
   */
  // Created: 2018-11-16 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthPasswordExpirationEnabled = true;

  /**
   * Block the user after many failed login attempts and limit the rate of consecutive failed attempts.
   */
  // Created: 2018-12-19 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthLoginRateLimitingEnabled = true;

  /**
   * Allow the user to request RefreshTokens to use in API automation.
   */
  // Created: 2019-04-04 fpagliaricci
  export const personalAccessTokensEnabled = false;

  /**
   * Rewrite the permissions dialog in React
   */
  // Created: 2018-12-14 jvanduyne
  export const permissionsDialogReact = false;

  /**
   * Show a page of recommended vizzes.
   */
  // Created: 2018-12-20 dsimpson
  export const vizRecommendationsEnabled = false;

  /**
   * Sets the Home Landing Experience as the start page
   */
  // Created: 2019-01-16 jwonglam
  export const homePage = true;

  /**
   * Allows admins to set a default start page at the site level
   */
  // Created: 2019-01-28 jwonglam
  export const siteDefaultStartPage = true;

  /**
   * Enables the new Flow Thumbnails to show up on cards, with new flow metadata card
   */
  // Created: 2019-01-30 jjia
  export const flowThumbnailsEnabled = false;

  /**
   * Enables functionality pertaining to PDF Subscriptions SP1
   */
  // Created: 2019-01-31 edwang
  export const pdfsubscriptionsSP1Enabled = true;

  /**
   * Enables components and UI pertaining to PDF Subscriptions SP1
   */
  // Created: 2019-04-18 toshiro
  export const pdfsubscriptionsUIEnabled = true;

  /**
  * Enables datasource usage analytics
  */
  // Created: 2019-02-13 qzhang
  export const nldatasourceAnalyticsEnabled = false;

  /**
   * Enables components and UI pertaining to DataRole content type
   */
  // Created: 2019-02-13 mjaiswal
  export const nlsemanticsServiceCustomDataRoleEnabled = true;

  /*
   * Display Tableau IBA license usage summary if enabled
   */
  // Created: 2019-03-01 antsmith
  export const ibaReportingEnabled = false;

  /**
   * Enables the Personal Sandbox
   */
  export const sandbox = false;

  /**
   * Enable/Disable the the named Sharing feature
   * This feature is gated by FF and site setting and so this flag will only be directly used in site setting
   * For everything else use Serverservice.isNamedSharingEnabled()
   */
  // Created: 2019-02-27 sanaik
  export const namedSharingEnabled = false;

  /**
   * Shows the mobile biometrics setting
   */
  // created: 2019-03-18 mmathews
  export const showMobileBiometricsSetting = false;

  /**
   * Shows Extract Move To The Web flag
   */
  // created: 2019-03-26 biliu
  export const extractMoveToTheWebEnabled = false;

  /*
   * Display the search space
   */
  // Created: 2019-04-04 bmoulin
  export const searchSpace = true;

  /*
  * Share dialog in Vizportal Client for sharing view, workbook, project, datasource
  * Created: 2019-04-11 rkandhal
  */
  export const newShareDialogEnabled = false;

  /**
   * Feature flag for flow web edit
   */
  // Created: 2019-04-15 anlin
  export const MaestroFlowEditingServerV1 = false;
   /*
   * Enables the sign in note
   */
  // Created: 2019-04-17 rjoshi
  export const signInNoteEnabled = false;

  /**
   * Enables AFE controls in the settings page
   */
  export const alternativeFederationEngineUserInterface = false;

  /**
   * Enables EAR for flows
   */
  // Created: 2019-05-10 vhalitsyn
  export const maestroEarEnabled = false;

  /**
   * Created: 2019-07-03 odangat
   */
  export const setCertificationEnabled = true;
}

// Override the feature flags as soon as possible after the page loads and the feature flag defaults
// are set, before the application is initialized. The helper must be invoked after declaration of
// the feature flags, otherwise there will not be existing values to overwrite
if (window.location.search) {
  FeatureFlagsHelper.overwriteFeatureFlagsFromQueryString(window.location.search, FeatureFlags);
}
